import { useState, useMemo, useRef, useEffect } from 'react';

export function useVenueSearch(allVenues, filters, setFilters) {
  const [venueSearch, setVenueSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);
  
  // Wrap selectedVenues in useMemo to prevent unnecessary re-renders
  const selectedVenues = useMemo(() => {
    return filters.venueSearch || [];
  }, [filters.venueSearch]);

  const venueSuggestions = useMemo(() => {
    if (!venueSearch) return [];
    const lowerSearch = venueSearch.toLowerCase();
    const selectedSet = new Set(selectedVenues);
    return allVenues.filter(
      venue =>
        !selectedSet.has(venue) &&
        venue.toLowerCase().includes(lowerSearch)
    );
  }, [venueSearch, allVenues, selectedVenues]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setVenueSearch(value);
    setShowSuggestions(value.length > 0);
  };

  const handleSuggestionClick = (venue) => {
    if (!selectedVenues.includes(venue)) {
      // Update the filters state instead of internal state
      setFilters(prevFilters => ({
        ...prevFilters,
        venueSearch: [...(prevFilters.venueSearch || []), venue]
      }));
    }
    setVenueSearch('');
    setShowSuggestions(false);
  };

  const handleRemoveSelectedVenue = (venueToRemove) => {
    // Update the filters state instead of internal state
    setFilters(prevFilters => ({
      ...prevFilters,
      venueSearch: (prevFilters.venueSearch || []).filter(venue => venue !== venueToRemove)
    }));
  };

  // This function is no longer needed as reset happens through main filters reset
  // but kept for API compatibility
  const resetVenueSearch = () => {
    setVenueSearch('');
    setShowSuggestions(false);
    // No need to reset selectedVenues as it comes from filters
  };

  return {
    venueSearch,
    setVenueSearch,
    selectedVenues,
    showSuggestions,
    setShowSuggestions,
    searchContainerRef,
    venueSuggestions,
    handleSearchChange,
    handleSuggestionClick,
    handleRemoveSelectedVenue,
    resetVenueSearch,
  };
}
